import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import groupBy from "lodash/groupBy";
import {
  Step,
  State as StepState,
  isCanceled,
  isFailed,
  isPassed,
  isRunning,
  isScheduled,
  isIgnored,
  isSoftFailed,
  isWaitingForDependencies,
  isWaitingForInput,
} from "~/lib/pipeline";
import { Job, CommandJob, TriggerJob } from "~/components/build/Show/lib/types";
import { BuildStates, isTerminalBuildState } from "~/constants/BuildStates";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useMemo } from "react";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";
import {
  FilterByOption,
  StateOrder,
  JOB_STATE_FILTERS,
} from "~/components/build/Show/lib/filterConstants";

// Re-export for backwards compatibility
export { FilterByOption, StateOrder };

export enum DisplayMode {
  State = "state",
  Pipeline = "pipeline",
}

const DEFAULT_DISPLAY_MODE = DisplayMode.State;

interface FilterState {
  displayMode: DisplayMode | null;
  setDisplayMode: (displayMode: DisplayMode | null) => void;

  filterBy: FilterByOption[];
  setFilterBy: (filters: FilterByOption[]) => void;
  resetFilters: () => void;
  resetAll: () => void;
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set) => ({
      displayMode: DEFAULT_DISPLAY_MODE,
      filterBy: [],
      setDisplayMode: (displayMode) => set({ displayMode: displayMode }),
      setFilterBy: (filterBy) => set({ filterBy }),
      resetAll: () => set({ filterBy: [], displayMode: null }),
      resetFilters: () => {
        set({ filterBy: [] });
      },
    }),
    {
      name: "build-filters",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ displayMode: state.displayMode }),
      version: 2,
    },
  ),
);

type Build = {
  state: BuildStates;
};

/**
 * Get relevant job filters for a given step based on the current step and build state.
 *
 * This is used to determine which jobs should be shown for matrix and parallel steps when
 * the job state is not the same as the step state.
 */
export const getRelevantSubFiltersForStep = (step: Step, build: Build) => {
  const filter = getRelevantFilterForStep(step);
  if (!filter) {
    return [];
  }

  const idx = StateOrder.indexOf(filter);
  const filters = StateOrder.slice(0, idx + 1);

  return filters;
};

/**
 * Get the relevant filter for a given step based on the step state.
 *
 * In some ways this is just flattening out the state into something more manageable.
 */
export const getRelevantFilterForStep = (step: Step) => {
  if (isFailed(step)) {
    return FilterByOption.Failed;
  }
  if (isPassed(step)) {
    return FilterByOption.Passed;
  }
  if (isRunning(step)) {
    return FilterByOption.Running;
  }
  if (isScheduled(step)) {
    return FilterByOption.Scheduled;
  }
  if (isSoftFailed(step)) {
    return FilterByOption.SoftFailed;
  }
  if (isWaitingForDependencies(step) || isWaitingForInput(step)) {
    return FilterByOption.Waiting;
  }
  if (isCanceled(step)) {
    return FilterByOption.Canceled;
  }
  if (isIgnored(step)) {
    return FilterByOption.Ignored;
  }
  return null;
};

const STEP_FILTER_FUNCTIONS: Record<FilterByOption, (step: Step, build: Build) => boolean> = {
  [FilterByOption.Failed]: function (step: Step) {
    return isFailed(step);
  },
  [FilterByOption.Passed]: function (step: Step) {
    return isPassed(step);
  },
  [FilterByOption.Running]: function (step: Step) {
    return isRunning(step);
  },
  [FilterByOption.Scheduled]: function (step: Step) {
    return isScheduled(step);
  },
  [FilterByOption.SoftFailed]: function (step: Step) {
    return isSoftFailed(step);
  },
  [FilterByOption.Waiting]: function (step: Step) {
    return isWaitingForDependencies(step) || isWaitingForInput(step);
  },
  [FilterByOption.Canceled]: function (step: Step) {
    return isCanceled(step);
  },
  [FilterByOption.Ignored]: function (step: Step) {
    return isIgnored(step);
  },
};

function isMissingDependencies(job: CommandJob | TriggerJob, build: Build): boolean {
  const missingDependencies =
    useStepDependenciesStore.getState().dependenciesByStep[job.stepUuid || ""]
      ?.missing_dependencies || [];

  if (missingDependencies.length > 0 && isTerminalBuildState(build.state)) {
    return true;
  }

  return false;
}

export function isJobFailed(job: Job, build: Build): boolean {
  if (job.type === "script" || job.type === "trigger") {
    return isMissingDependencies(job, build) || JOB_STATE_FILTERS[FilterByOption.Failed](job);
  }
  return false;
}

const JOB_FILTER_FUNCTIONS: Record<FilterByOption, (job: Job, build: Build) => boolean> = {
  [FilterByOption.Failed]: function (job: Job, build: Build) {
    return isJobFailed(job, build);
  },
  [FilterByOption.Passed]: function (job: Job) {
    if (job.type === "script" || job.type === "trigger") {
      return JOB_STATE_FILTERS[FilterByOption.Passed](job);
    }
    return job.state === "finished";
  },
  [FilterByOption.Running]: function (job: Job) {
    return JOB_STATE_FILTERS[FilterByOption.Running](job);
  },
  [FilterByOption.Scheduled]: function (job: Job) {
    return JOB_STATE_FILTERS[FilterByOption.Scheduled](job);
  },
  [FilterByOption.SoftFailed]: function (job: Job) {
    return JOB_STATE_FILTERS[FilterByOption.SoftFailed](job);
  },
  [FilterByOption.Waiting]: function (job: Job) {
    return JOB_STATE_FILTERS[FilterByOption.Waiting](job);
  },
  [FilterByOption.Canceled]: function (job: Job) {
    return JOB_STATE_FILTERS[FilterByOption.Canceled](job);
  },
  [FilterByOption.Ignored]: function () {
    return false;
  },
};

/**
 * Filters steps based on the provided filters.
 *
 * If no filters are provided, it collapses consecutive wait steps.
 */
export function filterSteps(steps: Step[], filters: FilterByOption[], build: Build): Step[] {
  // Do not filter out ignored/skipped steps - they should be visible in the sidebar
  // (but not in table/waterfall views, which use different filtering)

  if (filters.length === 0) {
    // Collapse consecutive wait steps
    return steps.reduce<Step[]>((acc, step) => {
      // Filter out ignored steps by default
      if (isIgnored(step)) {
        return acc;
      }

      const prevStep = acc[acc.length - 1];

      // Collapse consecutive wait steps
      if (prevStep?.type === "wait" && step.type === "wait") {
        return acc;
      }

      return acc.concat(step);
    }, []);
  }

  const filterFns = filters.map((filter) => STEP_FILTER_FUNCTIONS[filter]);
  return steps.filter((step) => {
    // Remove wait steps from the list
    if (step.type === "wait") {
      return false;
    }

    // Apply all filters using logical OR operation
    return filterFns.some((filter) => filter(step, build));
  });
}

export function filterJobs<TJob extends Job>(
  jobs: TJob[],
  filters: FilterByOption[],
  build: Build,
): TJob[] {
  if (filters.length < 1) {
    return jobs;
  }

  return jobs.filter((job) => {
    // Apply all filters using logical OR operation
    return filters.some((filter) => {
      return JOB_FILTER_FUNCTIONS[filter](job, build);
    });
  });
}

export function groupStepsByState(steps: Step[], build: Build) {
  return groupBy(steps, (step) => {
    return getRelevantFilterForStep(step);
  });
}

type Counts = Record<FilterByOption, number>;
export const useStateCounts = (): Counts => {
  const { build } = useBuild();

  return useMemo(() => {
    const stats = build.statistics;
    return {
      [FilterByOption.Failed]: stats?.stepsFailedCount || 0,
      [FilterByOption.Running]: stats?.stepsRunningCount || 0,
      [FilterByOption.Passed]: stats?.stepsPassedCount || 0,
      [FilterByOption.Canceled]: stats?.stepsCanceledCount || 0,
      [FilterByOption.Waiting]: stats?.stepsWaitingCount || 0,
      [FilterByOption.Scheduled]: stats?.stepsScheduledCount || 0,
      [FilterByOption.SoftFailed]: stats?.stepsSoftFailedCount || 0,
      [FilterByOption.Ignored]: stats?.stepsIgnoredCount || 0,
    };
  }, [build]);
};

export function filterStepJobs<T extends Job>(jobs: T[], step, build): T[] {
  const filters = getRelevantSubFiltersForStep(step, build);
  return filterJobs(jobs, filters, build);
}

export function sortSteps(steps: Step[]) {
  // eslint-disable-next-line id-length
  const sortedSteps = steps.sort((a, b) => {
    const aSortKey = a.sortKey || "";
    const bSortKey = b.sortKey || "";
    return aSortKey.localeCompare(bSortKey);
  });
  return sortedSteps;
}
